import * as THREE from 'three'
import { TweenLite, TimelineLite, Power2 } from 'gsap'
import { EffectComposer, RenderPass, SMAAPass } from 'postprocessing'
import * as dat from 'dat.gui'

import RGBOffsetPass from './Passes/RGBOffsetPass/'
import GlitchPass from './Passes/GlitchPass/'

import EventEmitter from './Utils/EventEmitter.js'
import Time from './Utils/Time.js'
import Sizes from './Utils/Sizes'
import Cursor from './Utils/Cursor.js'
import Loader from './Loader.js'
import World from './World.js'
import Camera from './Cameras/GuidedCamera'
// import Camera from './Cameras/FreeCamera'

import glitchGradientTexture from './images/glitch-gradient.png'
import heroTexture from './images/hero.jpg'
import heroMaskTexture from './images/hero-mask.png'
import heroPortraitTexture from './images/hero-portait.jpg'
import heroMaskPortraitTexture from './images/hero-mask-portait.png'
import smaaArea from './smaa/area.png'
import smaaSearch from './smaa/search.png'
import terrainTexture from './images/terrain-texture.png'
import terrainTransitionTexture from './images/terrain-transition-texture.png'
import investigateTerrainTexture from './images/investigate-terrain-texture.png'
import heatGradientTexture from './images/heat-gradient-texture.png'
import validateTabletScreen1Texture from './images/validate-tablet-screen-1.png'
import validateTabletScreen2Texture from './images/validate-tablet-screen-2.png'
import validateTabletScreen3Texture from './images/validate-tablet-screen-3.png'
import validateTabletScreen4Texture from './images/validate-tablet-screen-4.png'
import validateTabletScreen5Texture from './images/validate-tablet-screen-5.png'
import validateTabletScreen6Texture from './images/validate-tablet-screen-6.png'
import investigateTopographyLayer1Texture from './images/investigate-topography-layer-1.png'
import investigateTopographyLayer2Texture from './images/investigate-topography-layer-2.png'
import investigateTopographyLayer3Texture from './images/investigate-topography-layer-3.png'
import investigateTopographyLayer4Texture from './images/investigate-topography-layer-4.png'
import investigateTopographyLayer5Texture from './images/investigate-topography-layer-5.png'
import investigateTopographyLayer6Texture from './images/investigate-topography-layer-6.png'

import topography1ModelObj from './models/topography-1.obj'
import topography2ModelObj from './models/topography-2.obj'
import topography3ModelObj from './models/topography-3.obj'
import topography4ModelObj from './models/topography-4.obj'
import topography5ModelObj from './models/topography-5.obj'
import topography6ModelObj from './models/topography-6.obj'
import droneBaseModelObj from './models/drone-base.obj'
import droneRotorModelObj from './models/drone-rotor.obj'
import droneBackRotorModelObj from './models/drone-back-rotor.obj'
import simulationTargetModelObj from './models/simulation-target.obj'
import simulationSceneModelObj from './models/simulation-scene.obj'
import simulationPillarModelObj from './models/simulation-pillar.obj'
import simulationStructureModelObj from './models/simulation-structure.obj'
import simulationAreaModelObj from './models/simulation-area.obj'
import simulatorRoomModelObj from './models/simulator-room.obj'
import simulatorOutsideModelObj from './models/simulator-outside.obj'
import simulatorInsideModelObj from './models/simulator-inside.obj'
import tabletModelObj from './models/tablet.obj'
import tabletScreenModelObj from './models/tablet-screen.obj'
import factoryRoomModelObj from './models/factory-room.obj'
import tankModelObj from './models/tank.obj'
import tankInsideModelObj from './models/tank-inside.obj'
import tankErrorModelObj from './models/tank-error.obj'
import roverModelObj from './models/rover.obj'
import nanopixModelObj from './models/nanopix.obj'
import factoryModelObj from './models/factory.obj'
import barrelModelObj from './models/barrel.obj'
import barrelsSceneFloorModelObj from './models/barrels-scene-floor.obj'
import barrelsSceneDelimiterModelObj from './models/barrels-scene-delimiter.obj'
import barrelsSceneRoomModelObj from './models/barrels-scene-room.obj'

import heroVertexShader from './shaders/hero.vertex.glsl'
import heroFragmentShader from './shaders/hero.fragment.glsl'

export default class Experience extends EventEmitter
{
    /**
     * Constructor
     */
    constructor(_options = {})
    {
        super()

        this.$eventHub = _options.$eventHub
        this.grid = _options.grid
        this.mobile = _options.mobile
        this.$canvas = _options.$canvas
        this.config = _options.config
        this.useComposer = _options.useComposer
        this.debug = _options.debug

        this.time = new Time()
        this.sizes = new Sizes()
        this.cursor = new Cursor()
        this.scene = new THREE.Scene()

        this.setUI()
        this.setRoute()
        this.setHero()
        this.setLoading()

        window.experience = this
    }

    setRoute()
    {
        this.route = {}
        this.route.current = null
        this.route.old = null
    }

    updateRoute(_route)
    {
        this.route.old = this.route.current
        this.route.current = _route

        // Is partialy loaded
        if(this.loading.step >= 1)
        {
            if(this.camera.type === 'guided')
            {
                this.camera.scroll.destination = this.route.current.meta.experienceCameraScrollDestination
            }

            if(this.route.old.name === 'home')
            {
                this.hero.showTimeline.pause()
                this.hero.firstShowTimeline.pause()
                this.hero.hideTimeline.restart()
            }
            if(this.route.current.name === 'home')
            {
                this.hero.hideTimeline.pause()
                this.hero.firstShowTimeline.pause()
                this.hero.showTimeline.restart()
            }

            if(!this.menuGlitchAnimation.debug)
            {
                if(this.route.current.name === 'menu')
                {
                    this.menuGlitchAnimation.hideTimeline.pause()

                    this.menuGlitchAnimation.showTimeline.seek(0)
                    this.menuGlitchAnimation.showTimeline.play()
                }
                else if(this.route.old.name === 'menu')
                {
                    this.menuGlitchAnimation.showTimeline.pause()

                    this.menuGlitchAnimation.hideTimeline.seek(0)
                    this.menuGlitchAnimation.hideTimeline.play()
                }
            }

            if(!this.transitionGlitchAnimation.debug)
            {
                const contentPages = ['protect', 'validate', 'training', 'investigate']
                if(contentPages.indexOf(this.route.current.name) !== -1 && this.route.old.name === 'slider')
                {
                    this.passes.transitionGlitch.enabled = true
                    this.passes.updateRenderToScreen()

                    this.transitionGlitchAnimation.transitionTimeline.seek(0)
                    this.transitionGlitchAnimation.transitionTimeline.play()
                }
                if(this.route.current.name === 'slider' && contentPages.indexOf(this.route.old.name) !== -1)
                {
                    this.passes.transitionGlitch.enabled = true
                    this.passes.updateRenderToScreen()

                    this.transitionGlitchAnimation.transitionTimeline.seek('-=0')
                    this.transitionGlitchAnimation.transitionTimeline.reverse()
                }
            }
        }

        // Is fully loaded
        if(this.loading.step === 2)
        {
            // Stop interaction
            if(this.route.old && this.route.old.meta.experienceInteraction)
            {
                this.$eventHub.$emit(`experience.${this.route.old.meta.experienceInteraction}.stop`)
            }

            // Start interaction
            if(this.route.current.meta.experienceInteraction)
            {
                this.$eventHub.$emit(`experience.${this.route.current.meta.experienceInteraction}.start`)
            }

            if(this.route.current.name === 'slider')
            {
                if(this.route.old.name === 'home')
                {
                    window.setTimeout(() =>
                    {
                        this.world.playIntro()
                    }, 1000)
                }

                if(this.route.old.meta.experienceFocusScene)
                {
                    this.world.quitFocus()
                }
            }

            if(this.route.current.meta.experienceFocusScene)
            {
                this.world.playFocus()
            }
        }
    }

    /**
     * Set UI
     */
    setUI()
    {
        if(this.debug)
        {
            this.ui = new dat.GUI()
            this.ui.width = 480

            // dat.GUI.toggleHide()
        }
    }

    /**
     * Set loading
     */
    setLoading()
    {
        this.loading = {}
        this.loading.step = 0
        this.loading.loader = new Loader()

        // Load first assets
        this.loading.loader.load(glitchGradientTexture, 'glitchGradientTexture', 'image')
        this.loading.loader.load(smaaArea, 'smaaArea', 'image')
        this.loading.loader.load(smaaSearch, 'smaaSearch', 'image')
        this.loading.loader.load(terrainTexture, 'terrainTexture', 'image')

        // Load end
        this.loading.loader.on('end', () =>
        {
            this.loading.step++

            // Step 1
            if(this.loading.step === 1)
            {
                // Set environment
                this.setEnvironment()

                if(this.route.current.name === 'home' || this.route.current.name === 'slider')
                {
                    this.world.playLoading()
                }
                else
                {
                    if(this.camera.type === 'guided')
                    {
                        const experienceFocusScene = this.route.current.meta.experienceFocusScene ? this.route.current.meta.experienceFocusScene : 'protect'
                        this.camera.setState(`${experienceFocusScene}Focus`, 0)
                    }
                }

                // Camera
                if(this.camera.type === 'guided')
                {
                    this.camera.scroll.destination = this.route.current.meta.experienceCameraScrollDestination
                }

                // Load rest of assets
                this.loading.loader.load(terrainTransitionTexture, 'terrainTransitionTexture', 'image')
                this.loading.loader.load(heatGradientTexture, 'heatGradientTexture', 'image')
                this.loading.loader.load(validateTabletScreen1Texture, 'validateTabletScreen1Texture', 'image')
                this.loading.loader.load(validateTabletScreen2Texture, 'validateTabletScreen2Texture', 'image')
                this.loading.loader.load(validateTabletScreen3Texture, 'validateTabletScreen3Texture', 'image')
                this.loading.loader.load(validateTabletScreen4Texture, 'validateTabletScreen4Texture', 'image')
                this.loading.loader.load(validateTabletScreen5Texture, 'validateTabletScreen5Texture', 'image')
                this.loading.loader.load(validateTabletScreen6Texture, 'validateTabletScreen6Texture', 'image')
                this.loading.loader.load(investigateTopographyLayer1Texture, 'investigateTopographyLayer1Texture', 'image')
                this.loading.loader.load(investigateTopographyLayer2Texture, 'investigateTopographyLayer2Texture', 'image')
                this.loading.loader.load(investigateTopographyLayer3Texture, 'investigateTopographyLayer3Texture', 'image')
                this.loading.loader.load(investigateTopographyLayer4Texture, 'investigateTopographyLayer4Texture', 'image')
                this.loading.loader.load(investigateTopographyLayer5Texture, 'investigateTopographyLayer5Texture', 'image')
                this.loading.loader.load(investigateTopographyLayer6Texture, 'investigateTopographyLayer6Texture', 'image')
                this.loading.loader.load(investigateTerrainTexture, 'investigateTerrainTexture', 'image')
                this.loading.loader.load(topography1ModelObj, 'topography1ModelObj', 'objx')
                this.loading.loader.load(topography2ModelObj, 'topography2ModelObj', 'objx')
                this.loading.loader.load(topography3ModelObj, 'topography3ModelObj', 'objx')
                this.loading.loader.load(topography4ModelObj, 'topography4ModelObj', 'objx')
                this.loading.loader.load(topography5ModelObj, 'topography5ModelObj', 'objx')
                this.loading.loader.load(topography6ModelObj, 'topography6ModelObj', 'objx')
                this.loading.loader.load(droneBaseModelObj, 'droneBaseModelObj', 'objx')
                this.loading.loader.load(droneRotorModelObj, 'droneRotorModelObj', 'objx')
                this.loading.loader.load(droneBackRotorModelObj, 'droneBackRotorModelObj', 'objx')
                this.loading.loader.load(simulationTargetModelObj, 'simulationTargetModelObj', 'objx')
                this.loading.loader.load(simulationSceneModelObj, 'simulationSceneModelObj', 'objx')
                this.loading.loader.load(simulationPillarModelObj, 'simulationPillarModelObj', 'objx')
                this.loading.loader.load(simulationStructureModelObj, 'simulationStructureModelObj', 'objx')
                this.loading.loader.load(simulationAreaModelObj, 'simulationAreaModelObj', 'objx')
                this.loading.loader.load(simulatorRoomModelObj, 'simulatorRoomModelObj', 'objx')
                this.loading.loader.load(simulatorOutsideModelObj, 'simulatorOutsideModelObj', 'objx')
                this.loading.loader.load(simulatorInsideModelObj, 'simulatorInsideModelObj', 'objx')
                this.loading.loader.load(tabletModelObj, 'tabletModelObj', 'objx')
                this.loading.loader.load(tabletScreenModelObj, 'tabletScreenModelObj', 'obj')
                this.loading.loader.load(factoryRoomModelObj, 'factoryRoomModelObj', 'objx')
                this.loading.loader.load(tankModelObj, 'tankModelObj', 'objx')
                this.loading.loader.load(tankInsideModelObj, 'tankInsideModelObj', 'obj')
                this.loading.loader.load(tankErrorModelObj, 'tankErrorModelObj', 'obj')
                this.loading.loader.load(roverModelObj, 'roverModelObj', 'objx')
                this.loading.loader.load(nanopixModelObj, 'nanopixModelObj', 'objx')
                this.loading.loader.load(factoryModelObj, 'factoryModelObj', 'objx')
                this.loading.loader.load(barrelModelObj, 'barrelModelObj', 'objx')
                this.loading.loader.load(barrelsSceneFloorModelObj, 'barrelsSceneFloorModelObj', 'objx')
                this.loading.loader.load(barrelsSceneDelimiterModelObj, 'barrelsSceneDelimiterModelObj', 'objx')
                this.loading.loader.load(barrelsSceneRoomModelObj, 'barrelsSceneRoomModelObj', 'objx')
            }

            // Step 2
            else if(this.loading.step === 2)
            {
                // Set terrain transitions
                this.world.setTerrainTransitions()

                // Set all scene contents
                for(const _scene of this.world.scenes.all)
                {
                    _scene.setContents()
                }

                // Start interaction
                if(this.route.current.meta.experienceInteraction)
                {
                    this.$eventHub.$emit(`experience.${this.route.current.meta.experienceInteraction}.start`)
                }

                // If on slider, play intro
                if(this.route.current.name === 'slider' || this.route.current.name === 'menu')
                {
                    this.world.playIntro()
                }

                // If on protect, play focus
                if(this.route.current.meta.experienceFocusScene)
                {
                    this.world.playFocus()
                    this.world.scenes.current.show()
                }

                this.setKeypoints()
            }
        })
    }

    /**
     * Set environment
     */
    setEnvironment()
    {
        // Renderer
        this.renderer = new THREE.WebGLRenderer({
            stencil: false,
            depth: true,
            alpha: true,
            canvas: this.$canvas
        })
        this.renderer.sortObjects = true
        this.renderer.setClearColor(0x05070f, 1)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        this.renderer.setSize(this.sizes.viewport.width, this.sizes.viewport.height)

        // Camera
        this.camera = new Camera({
            config: this.config,
            $eventHub: this.$eventHub,
            mobile: this.mobile,
            time: this.time,
            cursor: this.cursor,
            sizes: this.sizes,
            ui: this.ui,
            route: this.route
        })
        this.scene.add(this.camera.container)

        // Composer
        this.composer = new EffectComposer(this.renderer, { depthTexture: true })

        // Passes
        this.passes = {}
        this.passes.list = []
        this.passes.updateRenderToScreen = () =>
        {
            let enabledPassFound = false

            for(let i = this.passes.list.length - 1; i >= 0; i--)
            {
                const pass = this.passes.list[i]

                if(pass.enabled && !enabledPassFound)
                {
                    pass.renderToScreen = true
                    enabledPassFound = true
                }
                else
                {
                    pass.renderToScreen = false
                }
            }
        }

        this.passes.render = new RenderPass(this.scene, this.camera.instance)
        this.composer.addPass(this.passes.render)
        this.passes.list.push(this.passes.render)

        // this.passes.bokeh = new BokehPass(this.camera.instance, { focus: 0.15, dof: 0.01, aperture: 0.025, maxBlur: 0.006 })
        // this.passes.bokeh.enabled = false
        // this.composer.addPass(this.passes.bokeh)
        // this.passes.list.push(this.passes.bokeh)

        this.passes.smaa = new SMAAPass(this.loading.loader.resources.smaaSearch, this.loading.loader.resources.smaaArea)
        this.passes.smaa.enabled = window.devicePixelRatio <= 1
        this.composer.addPass(this.passes.smaa)
        this.passes.list.push(this.passes.smaa)

        this.passes.rgbOffset = new RGBOffsetPass({
            resolution: new THREE.Vector2(this.sizes.viewport.width, this.sizes.viewport.height),
            strength: 15,
            radialDumping: new THREE.Vector4(0.5, 0.5, 1.0, - 0.5),
            teint: new THREE.Vector3(1.165, 1.132, 1.274)
        })
        this.composer.addPass(this.passes.rgbOffset)
        this.passes.list.push(this.passes.rgbOffset)

        const gradientTexture = new THREE.Texture(this.loading.loader.resources.glitchGradientTexture)
        gradientTexture.needsUpdate = true
        this.passes.menuGlitch = new GlitchPass({
            gradient: gradientTexture
        })
        this.passes.menuGlitch.enabled = false
        this.composer.addPass(this.passes.menuGlitch)
        this.passes.list.push(this.passes.menuGlitch)

        this.passes.transitionGlitch = new GlitchPass({
            gradient: gradientTexture
        })
        this.passes.transitionGlitch.enabled = false
        this.composer.addPass(this.passes.transitionGlitch)
        this.passes.list.push(this.passes.transitionGlitch)

        this.passes.updateRenderToScreen()

        // World
        this.world = new World({
            $eventHub: this.$eventHub,
            route: this.route,
            mobile: this.mobile,
            camera: this.camera,
            loader: this.loading.loader,
            time: this.time,
            cursor: this.cursor,
            sizes: this.sizes,
            ui: this.ui,
            passes: this.passes
        })
        this.scene.add(this.world.container)

        // Intro end
        this.world.on('intro_end', () =>
        {
            if(this.route.current.name === 'protect')
            {
                this.experience.world.playFocus()
            }
        })

        /**
         * Menu glitch animation
         */
        this.menuGlitchAnimation = {}
        this.menuGlitchAnimation.tileOffset = new THREE.Vector2(0.1, 0.03)
        this.menuGlitchAnimation.debug = false

        if(this.menuGlitchAnimation.debug)
        {
            this.passes.menuGlitch.enabled = true
            this.passes.updateRenderToScreen()
        }

        this.passes.menuGlitch.material.uniforms.uTileOffset.value.x = 0.1
        this.passes.menuGlitch.material.uniforms.uTileOffset.value.y = 0.03
        this.passes.menuGlitch.material.uniforms.uTileAmplitude.value = 2
        this.passes.menuGlitch.material.uniforms.uGradientAmplitude.value = 0.5

        const menuGlitchUniforms = this.passes.menuGlitch.material.uniforms

        // Show timeline
        this.menuGlitchAnimation.showTimeline = new TimelineLite({ paused: true })
        this.menuGlitchAnimation.showTimeline.timeScale(0.7)

        this.menuGlitchAnimation.showTimeline.add(
            () =>
            {
                this.passes.menuGlitch.enabled = true
                this.passes.updateRenderToScreen()
            },
            0
        )
        this.menuGlitchAnimation.showTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uTileProgressHorizontal, 1.1, { value: 1 + menuGlitchUniforms.uTileAmplitude.value * 0.5 }, { value: - menuGlitchUniforms.uTileAmplitude.value * 0.5 }),
            0
        )
        this.menuGlitchAnimation.showTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uGradientProgress, 0.6, { value: 1 + menuGlitchUniforms.uGradientOffset.value }, { value: - menuGlitchUniforms.uGradientOffset.value * 0.5 - menuGlitchUniforms.uGradientAmplitude.value }),
            0.1
        )
        this.menuGlitchAnimation.showTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uBlueProgress, 0.6, { value: 1 + menuGlitchUniforms.uTileOffset.value.x }, { value: - menuGlitchUniforms.uTileOffset.value.x * 0.5 - menuGlitchUniforms.uGradientAmplitude.value }),
            0.125
        )
        this.menuGlitchAnimation.showTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uSaturation, 0.52, { value: 1 }, { value: 1.01 }),
            0
        )
        this.menuGlitchAnimation.showTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uSaturation, 0.1, { value: 1 }, { value: 2.5 }),
            0.32
        )
        this.menuGlitchAnimation.showTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uSaturation, 1.8, { value: 2.5 }, { value: 1 }),
            0.92
        )
        this.menuGlitchAnimation.showTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uWhiteTileStrength, 1, { value: 0 }, { value: 0.1 }),
            0.3
        )

        // Hide timeline
        this.menuGlitchAnimation.hideTimeline = new TimelineLite({ paused: true })
        this.menuGlitchAnimation.hideTimeline.timeScale(0.7)

        this.menuGlitchAnimation.hideTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uWhiteTileStrength, 1, { value: 0.1 }, { value: 0 }),
            0
        )
        this.menuGlitchAnimation.hideTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uSaturation, 0.4, { value: 1 }, { value: 1.5 }),
            0.4
        )
        this.menuGlitchAnimation.hideTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uSaturation, 1, { value: 1.5 }, { value: 1 }),
            0.8
        )
        this.menuGlitchAnimation.hideTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uBlueProgress, 0.6, { value: - menuGlitchUniforms.uTileOffset.value.x * 0.5 - menuGlitchUniforms.uGradientAmplitude.value }, { value: 1 + menuGlitchUniforms.uTileOffset.value.x }),
            0.2
        )
        this.menuGlitchAnimation.hideTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uTileProgressHorizontal, 0.9, { value: - menuGlitchUniforms.uTileAmplitude.value * 0.5 }, { value: 1 + menuGlitchUniforms.uTileAmplitude.value * 0.5 }),
            0.2
        )
        this.menuGlitchAnimation.hideTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uGradientProgress, 0.6, { value: - menuGlitchUniforms.uGradientOffset.value * 0.5 - menuGlitchUniforms.uGradientAmplitude.value }, { value: 1 + menuGlitchUniforms.uGradientOffset.value }),
            0.225
        )

        this.menuGlitchAnimation.hideTimeline.add(
            () =>
            {
                this.passes.menuGlitch.enabled = false
                this.passes.updateRenderToScreen()
            },
            1.8
        )

        // Transition timeline
        this.menuGlitchAnimation.transitionTimeline = new TimelineLite({ paused: true })

        this.menuGlitchAnimation.transitionTimeline.add(
            () =>
            {
                this.passes.menuGlitch.enabled = true
                this.passes.updateRenderToScreen()
            },
            0
        )

        this.menuGlitchAnimation.transitionTimeline.add(
            TweenLite.fromTo(menuGlitchUniforms.uTileProgressVertical, 3, { value: - menuGlitchUniforms.uTileAmplitude.value * 0.5 }, { value: 1 + menuGlitchUniforms.uTileAmplitude.value * 0.5 }),
            0
        )

        this.menuGlitchAnimation.transitionTimeline.add(
            () =>
            {
                this.passes.menuGlitch.enabled = true
                this.passes.updateRenderToScreen()
            },
            3
        )

        if(this.route.current.name === 'menu' && !this.menuGlitchAnimation.debug)
        {
            this.menuGlitchAnimation.showTimeline.progress(1)
        }

        /**
         * Transition glitch animation
         */
        this.transitionGlitchAnimation = {}
        this.transitionGlitchAnimation.tileOffset = new THREE.Vector2(0.1, 0.03)
        this.transitionGlitchAnimation.debug = false

        if(this.transitionGlitchAnimation.debug)
        {
            this.passes.transitionGlitch.enabled = true
            this.passes.updateRenderToScreen()
        }

        this.passes.transitionGlitch.material.uniforms.uTileOffset.value.x = 0.03
        this.passes.transitionGlitch.material.uniforms.uTileOffset.value.y = 0.1
        this.passes.transitionGlitch.material.uniforms.uTileAmplitude.value = 3
        this.passes.transitionGlitch.material.uniforms.uGradientAmplitude.value = 0

        const transitionGlitchUniforms = this.passes.transitionGlitch.material.uniforms

        this.transitionGlitchAnimation.transitionTimeline = new TimelineLite({
            paused: false,
            onComplete: () =>
            {
                this.passes.transitionGlitch.enabled = false
                this.passes.updateRenderToScreen()
            },
            onReverseComplete: () =>
            {
                this.passes.transitionGlitch.enabled = false
                this.passes.updateRenderToScreen()
            }
        })

        window.requestAnimationFrame(() =>
        {
            this.transitionGlitchAnimation.transitionTimeline.pause()
            this.transitionGlitchAnimation.transitionTimeline.seek(0)
        })

        this.transitionGlitchAnimation.transitionTimeline.add(
            TweenLite.fromTo(transitionGlitchUniforms.uTileProgressVertical, 2, { value: - transitionGlitchUniforms.uTileAmplitude.value * 0.5 }, { value: 1 + transitionGlitchUniforms.uTileAmplitude.value * 0.5, ease: Power2.easeInOut }),
            0
        )

        this.transitionGlitchAnimation.transitionTimeline.add(
            TweenLite.fromTo(transitionGlitchUniforms.uWhiteTileStrength, 0.75, { value: 0 }, { value: 0.02 }),
            0.25
        )

        this.transitionGlitchAnimation.transitionTimeline.add(
            TweenLite.fromTo(transitionGlitchUniforms.uWhiteTileStrength, 0.75, { value: 0.02 }, { value: 0 }),
            1
        )

        this.transitionGlitchAnimation.transitionTimeline.add(
            TweenLite.fromTo(transitionGlitchUniforms.uSaturation, 0.75, { value: 1 }, { value: 1.5 }),
            0.25
        )

        this.transitionGlitchAnimation.transitionTimeline.add(
            TweenLite.fromTo(transitionGlitchUniforms.uSaturation, 0.75, { value: 1.5 }, { value: 1 }),
            1
        )

        // Time tick
        this.time.on('tick', () =>
        {
            // Menu glitch animation
            if(this.menuGlitchAnimation.debug)
            {
                this.menuGlitchAnimation.transitionTimeline.progress(1 - this.cursor.ratio.y)
            }
            this.passes.menuGlitch.material.uniforms.uTime.value = this.time.elapsed

            // Transition glitch animation
            if(this.transitionGlitchAnimation.debug)
            {
                this.transitionGlitchAnimation.transitionTimeline.progress(1 - this.cursor.ratio.y)
            }
            this.passes.transitionGlitch.material.uniforms.uTime.value = this.time.elapsed

            // Renderer
            if(this.useComposer)
            {
                this.composer.render(this.scene, this.camera.instance)
            }
            else
            {
                this.renderer.render(this.scene, this.camera.instance)
            }

            // if(this.camera.type === 'guided')
            // {
            //     const panX = this.camera.pan.value.x / this.camera.pan.amplitude.x + 0.5
            //     const panY = this.camera.pan.value.y / this.camera.pan.amplitude.y + 0.5

            //     this.passes.rgbOffset.material.uniforms.uRadialDumping.value.x = panX
            //     this.passes.rgbOffset.material.uniforms.uRadialDumping.value.y = panY
            // }
        })

        // Resize event
        this.sizes.on('resize', () =>
        {
            this.camera.instance.aspect = this.sizes.viewport.width / this.sizes.viewport.height
            this.camera.instance.updateProjectionMatrix()

            this.renderer.setSize(this.sizes.viewport.width, this.sizes.viewport.height)

            if(this.useComposer)
            {
                this.passes.render.setSize(this.sizes.viewport.width, this.sizes.viewport.height)
                this.passes.smaa.setSize(this.sizes.viewport.width, this.sizes.viewport.height)
                this.passes.rgbOffset.setSize(this.sizes.viewport.width, this.sizes.viewport.height)
                this.composer.setSize(this.sizes.viewport.width, this.sizes.viewport.height)
            }
        })

        // UI
        if(this.ui)
        {
            const dummy = {}
            dummy.clearColor = '#05070f'

            // Folder
            const folder = this.ui.addFolder('teint')
            folder.open()

            folder.add(this.passes.rgbOffset.material.uniforms.uTeint.value, 'x', 0, 2, 0.001)
            folder.add(this.passes.rgbOffset.material.uniforms.uTeint.value, 'y', 0, 2, 0.001)
            folder.add(this.passes.rgbOffset.material.uniforms.uTeint.value, 'z', 0, 2, 0.001)
            folder.addColor(dummy, 'clearColor').onFinishChange(() =>
            {
                this.renderer.setClearColor(dummy.clearColor)
            })
        }
    }

    /**
     * Set hero
     */
    setHero()
    {
        this.hero = {}

        // Geometry
        this.hero.plane = new THREE.PlaneBufferGeometry(2, 2, 1, 1)

        // Textures
        this.hero.orientation = null
        this.hero.loaded = 0
        this.hero.loadCallback = () =>
        {
            this.hero.loaded++

            if(this.hero.loaded === 2)
            {
                this.hero.texture.needsUpdate = true
                this.hero.textureMask.needsUpdate = true

                this.hero.material.uniforms.uTextureRatio.value = this.hero.image.width / this.hero.image.height

                if(this.route.current.name === 'home')
                {
                    this.hero.firstShowTimeline.play()
                }
                else
                {
                    this.hero.hideTimeline.seek('-=0', false)
                }
            }
        }
        this.hero.image = document.createElement('img')
        this.hero.mask = document.createElement('img')
        this.hero.texture = new THREE.Texture(this.hero.image)
        this.hero.textureMask = new THREE.Texture(this.hero.mask)

        this.hero.image.addEventListener('load', this.hero.loadCallback)
        this.hero.mask.addEventListener('load', this.hero.loadCallback)

        // Cursor
        this.hero.cursor = {}
        this.hero.cursor.started = false
        this.hero.cursor.x = 0
        this.hero.cursor.y = 0

        this.time.on('tick', () =>
        {
            const targetX = this.cursor.ratio.fromCenter.x * 0.012
            const targetY = this.cursor.ratio.fromCenter.y * 0.012

            this.hero.cursor.x += (targetX - this.hero.cursor.x) * this.time.delta * 0.002
            this.hero.cursor.y += (targetY - this.hero.cursor.y) * this.time.delta * 0.002

            this.hero.material.uniforms.uCursorOffset.value.x = this.hero.cursor.x
            this.hero.material.uniforms.uCursorOffset.value.y = - this.hero.cursor.y
        })

        // Meterial
        this.hero.material = new THREE.ShaderMaterial({
            wireframe: false,
            depthTest: true,
            depthWrite: false,
            transparent: true,
            vertexShader: heroVertexShader,
            fragmentShader: heroFragmentShader,
            uniforms:
            {
                uAlpha: { value: 1 },
                uBackgroundAlpha: { value: 0 },
                uTexture: { value: this.hero.texture },
                uTextureMask: { value: this.hero.textureMask },
                uTextureRatio: { value: this.hero.image.width / this.hero.image.height },
                uRatio: { value: this.sizes.viewport.width / this.sizes.viewport.height },
                uViewportSize: { value: new THREE.Vector2(this.sizes.viewport.width, this.sizes.viewport.height) },
                uTileCountX: { value: this.grid.countX },
                uTileCountY: { value: this.grid.countY },
                uTileOffsetX: { value: this.grid.offsetX / this.sizes.viewport.width },
                uTileOffsetY: { value: this.grid.offsetY / this.sizes.viewport.height },
                uProgress: { value: 0 },
                uCursorOffset: { value: new THREE.Vector2() }
            }
        })

        // Mesh
        this.hero.mesh = new THREE.Mesh(this.hero.plane, this.hero.material)
        this.hero.mesh.frustumCulled = false
        this.hero.mesh.renderOrder = 9999
        this.hero.mesh.visible = false

        this.scene.add(this.hero.mesh)

        // Handle resize
        this.hero.resize = () =>
        {
            // Orientation
            const orientation = this.sizes.viewport.width > this.sizes.viewport.width || this.sizes.viewport.width >= 768 ? 'landscape' : 'portrait'

            if(orientation !== this.hero.orientation)
            {
                this.hero.loaded = 0
                this.hero.orientation = orientation

                if(this.hero.orientation === 'landscape')
                {
                    this.hero.image.src = heroTexture
                    this.hero.mask.src = heroMaskTexture
                }
                else
                {
                    this.hero.image.src = heroPortraitTexture
                    this.hero.mask.src = heroMaskPortraitTexture
                }
            }

            // Uniforms
            this.hero.material.uniforms.uRatio.value = this.sizes.viewport.width / this.sizes.viewport.height
            this.hero.material.uniforms.uViewportSize.value.x = this.sizes.viewport.width
            this.hero.material.uniforms.uViewportSize.value.y = this.sizes.viewport.height
            this.hero.material.uniforms.uTileCountX.value = this.grid.countX + (this.sizes.viewport.width - this.grid.countX * this.grid.amplitude) / this.grid.amplitude
            this.hero.material.uniforms.uTileCountY.value = this.grid.countY + (this.sizes.viewport.height - this.grid.countY * this.grid.amplitude) / this.grid.amplitude
            this.hero.material.uniforms.uTileOffsetX.value = this.grid.offsetX / this.sizes.viewport.width
            this.hero.material.uniforms.uTileOffsetY.value = (this.grid.amplitude - this.grid.offsetY) / this.sizes.viewport.height
        }

        this.sizes.on('resize', () =>
        {
            this.hero.resize()
        })
        this.hero.resize()

        this.hero.hideTimeline = new TimelineLite({
            paused: true,
            onComplete: () =>
            {
                this.hero.mesh.visible = false
            }
        })
        this.hero.hideTimeline.add(TweenLite.fromTo(this.hero.material.uniforms.uProgress, 1, { value: 1 }, { value: 0 }), 0)
        this.hero.hideTimeline.add(TweenLite.fromTo(this.hero.material.uniforms.uBackgroundAlpha, 2, { value: 1 }, { value: 0 }), 0)
        this.hero.hideTimeline.add(TweenLite.fromTo(this.hero.material.uniforms.uAlpha, 2, { value: 1 }, { value: 0 }), 1.5)

        this.hero.showTimeline = new TimelineLite({
            paused: true,
            onStart: () =>
            {
                this.hero.mesh.visible = true
            }
        })
        this.hero.showTimeline.add(TweenLite.fromTo(this.hero.material.uniforms.uProgress, 2, { value: 0 }, { value: 1 }), 1)
        this.hero.showTimeline.add(TweenLite.fromTo(this.hero.material.uniforms.uBackgroundAlpha, 4, { value: 0 }, { value: 1 }), 1.5)
        this.hero.showTimeline.add(TweenLite.fromTo(this.hero.material.uniforms.uAlpha, 1, { value: 0 }, { value: 1 }), 0)

        this.hero.firstShowTimeline = new TimelineLite({
            paused: true,
            onStart: () =>
            {
                this.hero.mesh.visible = true
            }
        })
        this.hero.firstShowTimeline.add(TweenLite.fromTo(this.hero.material.uniforms.uProgress, 2, { value: 0 }, { value: 1 }), 1.5)
        this.hero.firstShowTimeline.add(TweenLite.fromTo(this.hero.material.uniforms.uBackgroundAlpha, 3, { value: 0 }, { value: 1 }), 2)
        this.hero.firstShowTimeline.add(TweenLite.fromTo(this.hero.material.uniforms.uAlpha, 0.5, { value: 1 }, { value: 1 }), 0)
    }

    /**
     * Set keypoints
     */
    setKeypoints()
    {
        // Set up
        this.keypoints = {}
        this.keypoints.items = []
        this.keypoints.projector = new THREE.Vector3()

        // Add keypoint method
        this.keypoints.add = (_options = {}) =>
        {
            const item = {}
            item.$element = _options.$element
            item.object = new THREE.Object3D()
            item.object.position.x = _options.x
            item.object.position.y = _options.y
            item.object.position.z = _options.z

            item.x = 0
            item.y = 0

            _options.target.add(item.object)

            this.keypoints.items.push(item)

            return item
        }

        // Time tick event
        this.time.on('tick', () =>
        {
            for(const item of this.keypoints.items)
            {
                this.keypoints.projector.setFromMatrixPosition(item.object.matrixWorld)
                this.keypoints.projector.project(this.camera.instance)
                this.keypoints.projector.x = (this.keypoints.projector.x * this.sizes.viewport.width / 2) + this.sizes.viewport.width / 2
                this.keypoints.projector.y = - (this.keypoints.projector.y * this.sizes.viewport.height / 2) + this.sizes.viewport.height / 2

                item.x = this.keypoints.projector.x
                item.y = this.keypoints.projector.y
            }
        })

        this.trigger('keypoints_ready')
    }

    /**
     * Destroy
     */
    destroy()
    {
        this.time.off('tick')
        this.sizes.off('resize')

        this.renderer.dispose()

        if(this.ui)
        {
            this.ui.destroy()
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/app-experience/Experience/index.js